/**
 * Сумма прописью для документов: 100 → «Сто рублей», 110.5 → «Сто десять рублей 50 копеек».
 * Копейки — цифрами (как принято в платёжных документах); если их нет, не пишутся.
 */

/** Окончание метки суммы прописью: поле summa → метка {{summa_propis}}. */
export const PROPIS_SUFFIX = '_propis';

const ONES_M = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const ONES_F = ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const TEENS = ['десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать'];
const TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
const HUNDREDS = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];

/** Форма слова для числа: 1 рубль, 2 рубля, 5 рублей (11–14 — всегда «рублей»). */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const n100 = Math.abs(n) % 100;
  const n10 = n100 % 10;
  if (n100 >= 11 && n100 <= 14) return forms[2];
  if (n10 === 1) return forms[0];
  if (n10 >= 2 && n10 <= 4) return forms[1];
  return forms[2];
}

function triad(n: number, feminine: boolean): string[] {
  const words = [HUNDREDS[Math.floor(n / 100)]!];
  const rest = n % 100;
  if (rest >= 10 && rest < 20) words.push(TEENS[rest - 10]!);
  else words.push(TENS[Math.floor(rest / 10)]!, (feminine ? ONES_F : ONES_M)[rest % 10]!);
  return words.filter(Boolean);
}

const SCALES: Array<{ forms: [string, string, string]; feminine: boolean }> = [
  { forms: ['', '', ''], feminine: false },
  { forms: ['тысяча', 'тысячи', 'тысяч'], feminine: true },
  { forms: ['миллион', 'миллиона', 'миллионов'], feminine: false },
  { forms: ['миллиард', 'миллиарда', 'миллиардов'], feminine: false },
  { forms: ['триллион', 'триллиона', 'триллионов'], feminine: false },
];

/** Целое число словами, в мужском роде: 1021 → «одна тысяча двадцать один». */
export function integerInWords(value: number): string {
  if (value === 0) return 'ноль';
  const words: string[] = [];
  let n = Math.abs(Math.trunc(value));
  for (let i = 0; n > 0 && i < SCALES.length; i++) {
    const part = n % 1000;
    n = Math.floor(n / 1000);
    if (part === 0) continue;
    const scale = SCALES[i]!;
    words.unshift(...triad(part, scale.feminine), ...(i > 0 ? [plural(part, scale.forms)] : []));
  }
  return (value < 0 ? ['минус', ...words] : words).join(' ');
}

/** Сумма в рублях прописью; пусто, если значение не число. */
export function moneyInWords(value: unknown): string {
  const num = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value.replace(/\s/g, '').replace(',', '.')) : NaN;
  if (!Number.isFinite(num)) return '';
  const cents = Math.round(Math.abs(num) * 100);
  const rubles = Math.floor(cents / 100);
  const kop = cents % 100;
  const text = `${num < 0 && cents > 0 ? 'минус ' : ''}${integerInWords(rubles)} ${plural(rubles, ['рубль', 'рубля', 'рублей'])}${
    kop ? ` ${String(kop).padStart(2, '0')} ${plural(kop, ['копейка', 'копейки', 'копеек'])}` : ''
  }`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
